/**
 * CardV2 - Unified card component system
 *
 * Pure, idempotent, compositional card components with standardized styling.
 * Uses compound component pattern for flexible composition.
 *
 * Variants:
 * - default: Neutral colored header with muted icons (sidebar widgets)
 * - accent: Same as default but with accent-colored icons (main content cards)
 * - panel: Clean header with border separator only
 * - compact: Smaller padding for dense layouts
 *
 * @example
 * ```tsx
 * <CardV2>
 *   <CardV2.Header>
 *     <CardV2.HeaderIcon><Clock /></CardV2.HeaderIcon>
 *     <CardV2.HeaderTitle>Key Dates</CardV2.HeaderTitle>
 *     <CardV2.HeaderBadge>3 items</CardV2.HeaderBadge>
 *   </CardV2.Header>
 *   <CardV2.Content>
 *     <CardV2.DataRow label="Posted" value="Jan 15, 2025" />
 *     <CardV2.DataRow label="Due" value="Feb 28, 2025" highlight />
 *   </CardV2.Content>
 * </CardV2>
 * ```
 */
import * as React from 'react'

import { twMerge } from 'tailwind-merge'

// ============================================================================
// Types
// ============================================================================

type CardVariant = 'default' | 'accent' | 'panel' | 'compact'

interface CardContextValue {
  variant: CardVariant
}

interface CardV2Props {
  children: React.ReactNode
  variant?: CardVariant
  className?: string
  /** Optional HTML id attribute */
  id?: string
}

interface HeaderProps {
  children: React.ReactNode
  className?: string
}

interface HeaderIconProps {
  children: React.ReactNode
  className?: string
}

interface HeaderTitleProps {
  children: React.ReactNode
  className?: string
}

interface HeaderBadgeProps {
  children: React.ReactNode
  className?: string
}

interface HeaderActionsProps {
  children: React.ReactNode
  className?: string
}

interface ContentProps {
  children: React.ReactNode
  className?: string
  /** Remove default padding (useful for tables) */
  flush?: boolean
}

interface SectionProps {
  children: React.ReactNode
  className?: string
}

interface SectionHeaderProps {
  children: React.ReactNode
  icon?: React.ReactNode
  className?: string
}

interface DataRowProps {
  label: string
  value?: React.ReactNode
  code?: string
  highlight?: boolean
  className?: string
}

interface DataGridProps {
  children: React.ReactNode
  columns?: 1 | 2 | 3 | 4
  className?: string
}

// ============================================================================
// Context
// ============================================================================

const CardContext = React.createContext<CardContextValue | null>(null)

function useCardContext(): CardContextValue {
  const context = React.useContext(CardContext)
  if (!context) {
    throw new Error('Card sub-components must be used within CardV2')
  }
  return context
}

// ============================================================================
// Style Definitions
// ============================================================================

const containerStyles: Record<CardVariant, string> = {
  default:
    'overflow-hidden rounded-lg border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900',
  accent:
    'overflow-hidden rounded-lg border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900',
  panel:
    'overflow-hidden rounded-xl border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900',
  compact:
    'overflow-hidden rounded-lg border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900',
}

const headerStyles: Record<CardVariant, string> = {
  default:
    'border-b border-primary-100 bg-primary-50/50 px-4 py-3 dark:border-primary-800 dark:bg-primary-800/50',
  accent:
    'border-b border-primary-100 bg-primary-50/50 px-4 py-3 dark:border-primary-800 dark:bg-primary-800/50',
  panel: 'border-b border-primary-100 px-4 py-3 dark:border-primary-800',
  compact:
    'border-b border-primary-100 bg-primary-50/50 px-3 py-2 dark:border-primary-800 dark:bg-primary-800/50',
}

const contentStyles: Record<CardVariant, string> = {
  default: 'p-4',
  accent: 'p-4',
  panel: 'p-4',
  compact: 'p-3',
}

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * CardV2.Header - Card header with optional colored background
 */
const Header: React.FC<HeaderProps> = ({ children, className }) => {
  const { variant } = useCardContext()

  // Separate children into icon, title, badge, and actions
  const childArray = React.Children.toArray(children)
  const icon = childArray.find(
    (child) => React.isValidElement(child) && child.type === HeaderIcon,
  )
  const title = childArray.find(
    (child) => React.isValidElement(child) && child.type === HeaderTitle,
  )
  const badge = childArray.find(
    (child) => React.isValidElement(child) && child.type === HeaderBadge,
  )
  const actions = childArray.find(
    (child) => React.isValidElement(child) && child.type === HeaderActions,
  )
  const otherChildren = childArray.filter(
    (child) =>
      !React.isValidElement(child) ||
      (child.type !== HeaderIcon &&
        child.type !== HeaderTitle &&
        child.type !== HeaderBadge &&
        child.type !== HeaderActions),
  )

  return (
    <div className={twMerge(headerStyles[variant], className)}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          {icon}
          {title}
          {otherChildren}
        </div>
        <div className="flex items-center gap-2">
          {badge}
          {actions}
        </div>
      </div>
    </div>
  )
}
Header.displayName = 'CardV2.Header'

/**
 * CardV2.HeaderIcon - Icon in the header
 */
const HeaderIcon: React.FC<HeaderIconProps> = ({ children, className }) => {
  const { variant } = useCardContext()

  const iconStyles: Record<CardVariant, string> = {
    default:
      'flex h-4 w-4 shrink-0 items-center justify-center text-primary-400 dark:text-primary-500 [&>svg]:h-full [&>svg]:w-full',
    accent:
      'flex h-4 w-4 shrink-0 items-center justify-center text-accent-500 dark:text-accent-400 [&>svg]:h-full [&>svg]:w-full',
    panel:
      'flex h-4 w-4 shrink-0 items-center justify-center text-primary-400 dark:text-primary-500 [&>svg]:h-full [&>svg]:w-full',
    compact:
      'flex h-4 w-4 shrink-0 items-center justify-center text-primary-400 dark:text-primary-500 [&>svg]:h-full [&>svg]:w-full',
  }

  return (
    <span className={twMerge(iconStyles[variant], className)}>{children}</span>
  )
}
HeaderIcon.displayName = 'CardV2.HeaderIcon'

/**
 * CardV2.HeaderTitle - Title text in the header
 */
const HeaderTitle: React.FC<HeaderTitleProps> = ({ children, className }) => {
  const { variant } = useCardContext()

  const titleStyles: Record<CardVariant, string> = {
    default:
      'text-sm font-bold uppercase tracking-wider text-primary-500 dark:text-primary-400',
    accent:
      'text-sm font-bold uppercase tracking-wider text-primary-500 dark:text-primary-400',
    panel: 'text-sm font-bold text-primary-900 dark:text-primary-100',
    compact:
      'text-sm font-bold uppercase tracking-wider text-primary-500 dark:text-primary-400',
  }

  return (
    <h3 className={twMerge(titleStyles[variant], className)}>{children}</h3>
  )
}
HeaderTitle.displayName = 'CardV2.HeaderTitle'

/**
 * CardV2.HeaderBadge - Badge/count in the header (positioned right)
 */
const HeaderBadge: React.FC<HeaderBadgeProps> = ({ children, className }) => {
  return (
    <span
      className={twMerge(
        'rounded-full border border-primary-200 bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-500 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-400',
        className,
      )}
    >
      {children}
    </span>
  )
}
HeaderBadge.displayName = 'CardV2.HeaderBadge'

/**
 * CardV2.HeaderActions - Action buttons/elements in header (positioned right)
 */
const HeaderActions: React.FC<HeaderActionsProps> = ({
  children,
  className,
}) => {
  return (
    <div className={twMerge('flex items-center gap-1', className)}>
      {children}
    </div>
  )
}
HeaderActions.displayName = 'CardV2.HeaderActions'

/**
 * CardV2.Content - Main content area
 */
const Content: React.FC<ContentProps> = ({
  children,
  className,
  flush = false,
}) => {
  const { variant } = useCardContext()

  return (
    <div className={twMerge(flush ? '' : contentStyles[variant], className)}>
      {children}
    </div>
  )
}
Content.displayName = 'CardV2.Content'

/**
 * CardV2.Section - Logical section within content
 */
const Section: React.FC<SectionProps> = ({ children, className }) => {
  return <div className={twMerge('space-y-2', className)}>{children}</div>
}
Section.displayName = 'CardV2.Section'

/**
 * CardV2.SectionHeader - Section header within content
 */
const SectionHeader: React.FC<SectionHeaderProps> = ({
  children,
  icon,
  className,
}) => {
  return (
    <div
      className={twMerge(
        'mb-3 flex items-center gap-2 border-b border-primary-100 pb-2 dark:border-primary-800',
        className,
      )}
    >
      {icon && (
        <span className="flex h-4 w-4 shrink-0 items-center justify-center text-accent-600 dark:text-accent-400 [&>svg]:h-full [&>svg]:w-full">
          {icon}
        </span>
      )}
      <h4 className="text-sm font-semibold text-primary-600 dark:text-primary-400">
        {children}
      </h4>
    </div>
  )
}
SectionHeader.displayName = 'CardV2.SectionHeader'

/**
 * CardV2.DataRow - Label/value pair display
 */
const DataRow: React.FC<DataRowProps> = ({
  label,
  value,
  code,
  highlight = false,
  className,
}) => {
  const isEmpty = !value && !code
  const displayValue = value ?? '—'

  return (
    <div
      className={twMerge(
        'group flex flex-col items-start justify-start rounded-md p-2.5 transition-colors hover:bg-primary-50 dark:hover:bg-primary-800/50',
        className,
      )}
    >
      <span className="mb-1 text-sm font-medium text-primary-400 transition-colors group-hover:text-accent-500 dark:text-primary-500">
        {label}
      </span>
      <div className="flex w-full items-baseline gap-2">
        <span
          className={twMerge(
            'break-words text-sm font-medium leading-snug',
            isEmpty
              ? 'text-primary-300 dark:text-primary-600'
              : 'text-primary-800 dark:text-primary-200',
            highlight && 'font-semibold text-accent-700 dark:text-accent-400',
          )}
        >
          {displayValue}
        </span>
        {code && (
          <span className="ml-auto inline-flex shrink-0 items-center rounded border border-primary-200 bg-primary-100 px-1.5 py-0.5 font-mono text-xs font-medium text-primary-500 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-400">
            {code}
          </span>
        )}
      </div>
    </div>
  )
}
DataRow.displayName = 'CardV2.DataRow'

/**
 * CardV2.DataGrid - Grid layout for data rows
 */
const DataGrid: React.FC<DataGridProps> = ({
  children,
  columns = 1,
  className,
}) => {
  const columnClasses: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  }

  return (
    <div className={twMerge('grid gap-0.5', columnClasses[columns], className)}>
      {children}
    </div>
  )
}
DataGrid.displayName = 'CardV2.DataGrid'

// ============================================================================
// Main Component
// ============================================================================

/**
 * CardV2 - Unified card container
 *
 * Compound component for consistent card styling across opportunity pages.
 */
const CardV2: React.FC<CardV2Props> & {
  Header: typeof Header
  HeaderIcon: typeof HeaderIcon
  HeaderTitle: typeof HeaderTitle
  HeaderBadge: typeof HeaderBadge
  HeaderActions: typeof HeaderActions
  Content: typeof Content
  Section: typeof Section
  SectionHeader: typeof SectionHeader
  DataRow: typeof DataRow
  DataGrid: typeof DataGrid
  Skeleton: typeof CardSkeleton
} = ({ children, variant = 'default', className, id }) => {
  const contextValue: CardContextValue = { variant }

  return (
    <CardContext.Provider value={contextValue}>
      <div id={id} className={twMerge(containerStyles[variant], className)}>
        {children}
      </div>
    </CardContext.Provider>
  )
}

CardV2.displayName = 'CardV2'
CardV2.Header = Header
CardV2.HeaderIcon = HeaderIcon
CardV2.HeaderTitle = HeaderTitle
CardV2.HeaderBadge = HeaderBadge
CardV2.HeaderActions = HeaderActions
CardV2.Content = Content
CardV2.Section = Section
CardV2.SectionHeader = SectionHeader
CardV2.DataRow = DataRow
CardV2.DataGrid = DataGrid

// ============================================================================
// Skeleton Component
// ============================================================================

interface CardSkeletonProps {
  variant?: CardVariant
  /** Show header skeleton */
  showHeader?: boolean
  /** Header title width */
  headerWidth?: 'sm' | 'md' | 'lg'
  /** Number of content rows to show */
  rows?: number
  className?: string
}

const headerWidthClasses = {
  sm: 'w-20',
  md: 'w-32',
  lg: 'w-44',
}

/**
 * CardV2.Skeleton - Loading skeleton for cards
 */
const CardSkeleton: React.FC<CardSkeletonProps> = ({
  variant = 'default',
  showHeader = true,
  headerWidth = 'md',
  rows = 2,
  className,
}) => {
  return (
    <div className={twMerge(containerStyles[variant], className)}>
      {showHeader && (
        <div className={headerStyles[variant]}>
          <div className="flex items-center gap-1.5">
            <div className="h-4 w-4 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
            <div
              className={twMerge(
                'h-3 animate-pulse rounded bg-primary-200 dark:bg-primary-700',
                headerWidthClasses[headerWidth],
              )}
            />
          </div>
        </div>
      )}
      <div className={contentStyles[variant]}>
        <div className="space-y-3">
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="space-y-1.5">
              <div className="h-2.5 w-16 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
              <div className="h-4 w-32 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
CardSkeleton.displayName = 'CardV2.Skeleton'

CardV2.Skeleton = CardSkeleton

// ============================================================================
// Exports
// ============================================================================

export { CardV2 }
export type {
  CardV2Props,
  CardVariant,
  HeaderProps as CardHeaderProps,
  HeaderIconProps as CardHeaderIconProps,
  HeaderTitleProps as CardHeaderTitleProps,
  HeaderBadgeProps as CardHeaderBadgeProps,
  HeaderActionsProps as CardHeaderActionsProps,
  ContentProps as CardContentProps,
  SectionProps as CardSectionProps,
  SectionHeaderProps as CardSectionHeaderProps,
  DataRowProps as CardDataRowProps,
  DataGridProps as CardDataGridProps,
  CardSkeletonProps,
}
